import React, { useState } from 'react';
import api, { errorMessage, asArray } from '../api';
import { useAuth } from '../context/AuthContext';
import Avatar from './Avatar';
import PostMedia from './PostMedia';
import RichText from './RichText';
import { HeartIcon, CommentIcon, ShareIcon, BookmarkIcon } from './icons';
import { formatListTime, formatTime } from '../lib/datetime';

/** Ett inlägg med media, gillningar, kommentarer och svar. */
export default function PostCard({ post: initial, onDeleted, onViewProfile, onTag, onShare }) {
  const { user } = useAuth();
  const [post, setPost] = useState(initial);
  const [comments, setComments] = useState(null);
  const [draft, setDraft] = useState('');
  const [replyTo, setReplyTo] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const mine = post.user_id === user.id;

  /** Gilla och spara svarar med det nya laget, sa raknaren aldrig glider. */
  const toggle = async (what) => {
    const on = what === 'like' ? !post.liked : !post.saved;
    // Knappen ska svara direkt; svaret ratter till om det gick fel.
    setPost(prev => ({ ...prev, [what === 'like' ? 'liked' : 'saved']: on,
      like_count: what === 'like' ? prev.like_count + (on ? 1 : -1) : prev.like_count }));

    try {
      const res = on
        ? await api.post(`/posts/${post.id}/${what}`)
        : await api.delete(`/posts/${post.id}/${what}`);
      setPost(prev => ({ ...prev, ...res.data }));
    } catch (err) {
      setError(errorMessage(err));
      setPost(initial);
    }
  };

  const loadComments = async () => {
    if (comments) return setComments(null);
    try {
      const res = await api.get(`/posts/${post.id}/comments`);
      setComments(asArray(res.data));
    } catch (err) {
      setError(errorMessage(err, 'Kommentarerna kunde inte hämtas.'));
    }
    return undefined;
  };

  const addComment = async () => {
    const content = draft.trim();
    if (!content) return;

    setBusy(true);
    setError('');
    try {
      const res = await api.post(`/posts/${post.id}/comments`, {
        content,
        parentId: replyTo ? replyTo.id : undefined,
      });
      setComments(prev => [...(prev || []), res.data]);
      setPost(prev => ({ ...prev, comment_count: prev.comment_count + 1 }));
      setDraft('');
      setReplyTo(null);
    } catch (err) {
      setError(errorMessage(err, 'Kommentaren kunde inte sparas.'));
    } finally {
      setBusy(false);
    }
  };

  const removeComment = async (id) => {
    try {
      await api.delete(`/posts/comments/${id}`);
      // Ett borttaget forstainlagg tar sina svar med sig.
      setComments(prev => prev.filter(c => c.id !== id && c.parent_id !== id));
      setPost(prev => ({ ...prev, comment_count: Math.max(0, prev.comment_count - 1) }));
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const removePost = async () => {
    try {
      await api.delete(`/posts/${post.id}`);
      if (onDeleted) onDeleted(post.id);
    } catch (err) {
      setError(errorMessage(err));
    }
  };

  const roots = (comments || []).filter(c => !c.parent_id);
  const repliesTo = (id) => (comments || []).filter(c => c.parent_id === id);

  const renderComment = (comment, isReply = false) => (
    <div key={comment.id} className={isReply ? 'comment comment--reply' : 'comment'}>
      <Avatar src={comment.profile_picture} name={comment.username} size="sm" />
      <div className="comment__body">
        <span className="comment__meta">
          <strong>{comment.username}</strong>
          <span className="faint"> {formatTime(comment.created_at)}</span>
        </span>
        <p className="comment__text">
          <RichText text={comment.content} onTag={onTag} />
        </p>
        {!isReply && (
          <button className="comment__reply" onClick={() => setReplyTo(comment)}>Svara</button>
        )}
      </div>
      {(comment.user_id === user.id || mine) && (
        <button className="comment__remove" onClick={() => removeComment(comment.id)} aria-label="Ta bort">
          ×
        </button>
      )}
    </div>
  );

  return (
    <article className="post">
      <header className="post__header">
        <button
          className="post__author"
          onClick={() => onViewProfile && !mine && onViewProfile(post.user_id)}
          type="button"
        >
          <Avatar src={post.profile_picture} name={post.username} size="sm" />
          <span>
            <span className="post__name">{post.username}</span>
            <span className="post__time">
              {formatListTime(post.created_at)}
              {post.visibility === 'friends' && ' · Vänner'}
            </span>
          </span>
        </button>

        {mine && <button className="btn btn--ghost btn--sm" onClick={removePost}>Ta bort</button>}
      </header>

      <PostMedia post={post} />

      {/* Filen kan ha forsvunnit; da ar kortet inte tomt utan forklarat. */}
      {post.image_path && !post.content && (
        <p className="post__missing">Bilden är inte längre tillgänglig</p>
      )}

      <div className="post__actions">
        <button
          className={post.liked ? 'post__action is-on' : 'post__action'}
          onClick={() => toggle('like')}
          aria-pressed={post.liked}
        >
          <HeartIcon size={22} filled={post.liked} />
          <span>{post.like_count}</span>
        </button>

        <button className="post__action" onClick={loadComments}>
          <CommentIcon size={22} />
          <span>{post.comment_count}</span>
        </button>

        <button className="post__action" onClick={() => onShare && onShare(post)}>
          <ShareIcon size={22} />
        </button>

        <button
          className={post.saved ? 'post__action post__action--end is-on' : 'post__action post__action--end'}
          onClick={() => toggle('save')}
          aria-pressed={post.saved}
          aria-label={post.saved ? 'Sparad' : 'Spara'}
        >
          <BookmarkIcon size={22} filled={post.saved} />
        </button>
      </div>

      {post.content && (
        <p className="post__caption">
          <strong>{post.username}</strong>{' '}
          <RichText
            text={post.content}
            onTag={onTag}
            onMention={onViewProfile ? undefined : undefined}
          />
        </p>
      )}

      {error && <p className="notice notice--error">{error}</p>}

      {post.comment_count > 0 && !comments && (
        <button className="post__comments-toggle" onClick={loadComments}>
          Visa {post.comment_count} {post.comment_count === 1 ? 'kommentar' : 'kommentarer'}
        </button>
      )}

      {comments && (
        <div className="post__comments">
          {roots.map(comment => (
            <React.Fragment key={comment.id}>
              {renderComment(comment)}
              {repliesTo(comment.id).map(reply => renderComment(reply, true))}
            </React.Fragment>
          ))}

          {replyTo && (
            <p className="comment__replying">
              Svarar {replyTo.username}
              <button onClick={() => setReplyTo(null)} aria-label="Avbryt">×</button>
            </p>
          )}

          <div className="chat__input">
            <input
              value={draft}
              onChange={e => setDraft(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') addComment(); }}
              placeholder={replyTo ? `Svara ${replyTo.username}…` : 'Skriv en kommentar…'}
              maxLength={1000}
            />
            <button className="btn btn--primary btn--icon" onClick={addComment} disabled={busy} aria-label="Skicka">
              <ShareIcon size={18} />
            </button>
          </div>
        </div>
      )}
    </article>
  );
}
